// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import cancelBackOrder from "@guestsupply/App/Handlers/BackOrders/CancelBackOrder";
import loadBackOrders from "@guestsupply/App/Handlers/BackOrders/LoadBackOrders";
import setShipFromAltDCModalIsOpen from "@guestsupply/App/Handlers/BackOrders/SetShipFromAltDCModalIsOpen";
import updateBackorderParameter from "@guestsupply/App/Handlers/BackOrders/UpdateBackorderParameter";
import { BackOrderModel, GetBackOrdersParameters } from "@guestsupply/App/Services/BackorderManagementService";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getBackorders, getBackordersParameter } from "@guestsupply/App/Store/Selectors/BackorderSelectors";
import Pagination, { PaginationPresentationProps } from "@guestsupply/MobiusOverrides/Pagination/Pagination";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import OrderDetailPageTypeLink from "@guestsupply/Overrides/Components/OrderDetailPageTypeLink";
import { pluralize } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag/Tag";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const parameter = getBackordersParameter(state);
    const backOrders = getBackorders(state, parameter!);
    return {
        parameter,
        backOrders: backOrders?.value,
        isLoading: backOrders?.isLoading,
        pagination: JSON.parse((backOrders as any)?.properties?.pagination || "{}"),
    };
};

const mapDispatchToProps = {
    loadBackOrders,
    cancelBackOrder,
    updateBackorderParameter,
    setShipFromAltDCModalIsOpen,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BackorderManagementStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    filtersToggleWrapper?: InjectableCss;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    totalOrdersText?: TypographyProps;
    filtersWrapper?: InjectableCss;
    orderNumberWrapper?: InjectableCss;
    poNumberWrapper?: InjectableCss;
    filtersActionsWrapper?: InjectableCss;
    clearButton?: ButtonPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    pagination?: PaginationPresentationProps;
    paginationWrapper?: InjectableCss;
    titleText?: TypographyProps;
    orderWarehouseHeader?: DataTableHeaderProps;
    orderWarehouseCells?: DataTableCellProps;
    orderCustomerIdHeader?: DataTableHeaderProps;
    orderCustomerIdCells?: DataTableCellProps;
    orderNumberHeader?: DataTableHeaderProps;
    orderNumberCells?: DataTableCellProps;
    orderLineNumberHeader?: DataTableHeaderProps;
    orderLineNumberCells?: DataTableCellProps;
    orderProductErpHeader?: DataTableHeaderProps;
    orderProductErpCells?: DataTableCellProps;
    orderProductDescriptionHeader?: DataTableHeaderProps;
    orderProductDescriptionCells?: DataTableCellProps;
    orderQuantityHeader?: DataTableHeaderProps;
    orderQuantityCells?: DataTableCellProps;
    orderLineStatusHeader?: DataTableHeaderProps;
    orderLineStatusCells?: DataTableCellProps;
    orderActionsHeader?: DataTableHeaderProps;
    orderActionsCells?: DataTableCellProps;
    actionsWrapper?: InjectableCss;
    cancelProductText?: TypographyPresentationProps;
    cancelProductLink?: LinkPresentationProps;
    shipAltLink?: LinkPresentationProps;
    cancelProductModal?: ModalPresentationProps;
    cancelProductModalTitle?: TypographyPresentationProps;
    cancelProductConfirmButton?: ButtonPresentationProps;
    cancelProductCancelButton?: ButtonPresentationProps;
    poNumberHeader?: DataTableHeaderProps;
    poNumberCells?: DataTableCellProps;
}

// Xng-Changes: please check BackorderManagementExtensions.ts for styles
export const backorderManagementStyles: BackorderManagementStyles = {};

const styles = backorderManagementStyles;

const BackorderManagement = ({
    parameter,
    backOrders,
    isLoading,
    pagination,
    loadBackOrders,
    cancelBackOrder,
    updateBackorderParameter,
    setShipFromAltDCModalIsOpen,
}: Props) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const [orderNumber, setOrderNumber] = useState(parameter?.OrderNumber);
    const [poNumber, setPoNumber] = useState(parameter?.PoNumber);
    const [cancelOrderModal, setCancelOrderModal] = useState<BackOrderModel | boolean>(false);
    const [updateTimeoutId, setUpdateTimeoutId] = useState<number>();

    const toaster = useContext(ToasterContext);

    useEffect(() => {
        setOrderNumber(parameter?.OrderNumber);
        setPoNumber(parameter?.PoNumber);
    }, [parameter?.OrderNumber, parameter?.PoNumber]);

    const clearFilters = () => {
        setOrderNumber("");
        setPoNumber("");
        updateBackorderParameter({
            OrderNumber: "",
            PoNumber: "",
        });
    };

    const orderNumberChangeHandler = (OrderNumber: string) => {
        setOrderNumber(OrderNumber);
        if (updateTimeoutId) {
            clearTimeout(updateTimeoutId);
        }
        const time = setTimeout(() => {
            updateBackorderParameter({
                OrderNumber,
            });
        }, 250);
        setUpdateTimeoutId(time);
    };

    const poNumberChangeHandler = (PoNumber: string) => {
        setPoNumber(PoNumber);
        if (updateTimeoutId) {
            clearTimeout(updateTimeoutId);
        }
        const time = setTimeout(() => {
            updateBackorderParameter({
                PoNumber,
            });
        }, 250);
        setUpdateTimeoutId(time);
    };

    const setCurrentPage = (newPageIndex: number) => {
        if (newPageIndex !== pagination?.page) {
            updateBackorderParameter({
                page: newPageIndex,
            });
        }
    };

    const setResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        if (resultsPerPage !== pagination?.pageSize) {
            updateBackorderParameter({
                pageSize: resultsPerPage,
            });
        }
    };

    const cancelProductModalHandler = (backorder: BackOrderModel) => {
        setCancelOrderModal(backorder);
    };

    const cancelProduct = () => {
        cancelBackOrder({
            ...(cancelOrderModal as BackOrderModel),
            onSuccess: () => {
                loadBackOrders({ ...(parameter as GetBackOrdersParameters), preventCaching: true });
            },
            onError: error => {
                toaster.addToast({
                    body: error,
                    messageType: "danger",
                });
            },
        });
        setCancelOrderModal(false);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="backOrderTable">
            <StyledWrapper {...styles.filtersToggleWrapper}>
                <Button
                    {...styles.toggleFilterButton}
                    onClick={() => setFilterOpen(state => !state)}
                    aria-label="Filter"
                >
                    <ButtonIcon {...styles.toggleFilterButtonIcon} src={filterOpen ? Minus : Plus} />
                    <VisuallyHidden>{translate("Filter")}</VisuallyHidden>
                </Button>
                {!!pagination?.totalCount && (
                    <Typography {...styles.totalOrdersText}>
                        {pluralize(pagination?.totalCount, translate("order"), { showNumber: true })}
                    </Typography>
                )}
            </StyledWrapper>
            {filterOpen && (
                <StyledWrapper {...styles.filtersWrapper}>
                    <StyledWrapper {...styles.orderNumberWrapper}>
                        <TextField
                            value={orderNumber}
                            label={translate("Order #")}
                            placeholder={translate("Order Number")}
                            onChange={event => orderNumberChangeHandler(event.target.value)}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.poNumberWrapper}>
                        <TextField
                            value={poNumber}
                            label={translate("PO #")}
                            placeholder={translate("PO Number")}
                            onChange={event => poNumberChangeHandler(event.target.value)}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.filtersActionsWrapper}>
                        <Button {...styles.clearButton} onClick={() => clearFilters()} aria-label="Clear Filters">
                            {translate("Clear Filters")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {!filterOpen && (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    {parameter?.OrderNumber && (
                        <Tag {...styles.appliedFilterTag} onDelete={() => orderNumberChangeHandler("")}>
                            {`${translate("Order #")}: ${parameter?.OrderNumber}`}
                        </Tag>
                    )}
                    {parameter?.PoNumber && (
                        <Tag {...styles.appliedFilterTag} onDelete={() => poNumberChangeHandler("")}>
                            {`${translate("Po #")}: ${parameter?.PoNumber}`}
                        </Tag>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.paginationWrapper}>
                <Typography {...styles.titleText}>{translate("My Upcoming Orders")}</Typography>
            </StyledWrapper>
            {isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!isLoading && backOrders && backOrders.length ? (
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        {/* <DataTableHeader
                            {...styles.orderWarehouseHeader}
                            title={translate("Warehouse")}
                            data-test-selector="tableHeader_warehouse"
                        >
                            {translate("Warehouse", "warehouse")}
                        </DataTableHeader> */}
                        <DataTableHeader
                            {...styles.orderCustomerIdHeader}
                            title={translate("Customer ID")}
                            data-test-selector="tableHeader_customerId"
                        >
                            {translate("Customer ID", "customerId")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderNumberHeader}
                            title={translate("Order #")}
                            data-test-selector="tableHeader_webOrderNumber"
                        >
                            {translate("Order #", "webOrderNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderLineNumberHeader}
                            title={translate("Order Line #")}
                            data-test-selector="tableHeader_webOrderLineNumber"
                        >
                            {translate("Order Line #", "webOrderLineNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderProductErpHeader}
                            title={translate("Product #")}
                            data-test-selector="tableHeader_productErp"
                        >
                            {translate("Product #", "productErp")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderProductDescriptionHeader}
                            title={translate("Product Description")}
                            data-test-selector="tableHeader_productDescription"
                        >
                            {translate("Product Description", "productDescription")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderQuantityHeader}
                            title={translate("Quantity Backordered")}
                            data-test-selector="tableHeader_orderQuantity"
                        >
                            {translate("Quantity Backordered", "orderQuantity")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderLineStatusHeader}
                            title={translate("Order Line Status")}
                            data-test-selector="tableHeader_orderLineStatus"
                        >
                            {translate("Order Line Status", "orderLineStatus")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.poNumberHeader}
                            title={translate("PO Number")}
                            data-test-selector="tableHeader_poNumber"
                        >
                            {translate("PO Number", "poNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderActionsHeader}
                            title={translate("Actions")}
                            data-test-selector="tableHeader_orderActions"
                        >
                            {translate("Actions", "orderActions")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="backOrderTable_tableBody">
                        {backOrders.map(backorder => {
                            const {
                                id,
                                warehouse,
                                customerNumber,
                                orderNumber,
                                orderLineNumber,
                                productErpNumber,
                                productDescription,
                                backorderQuantity,
                                orderLineStatus,
                                cancelRequested,
                                availableAltWarehouse,
                                isShippedFromAlternateDc,
                                poNumber,
                            } = backorder;
                            return (
                                <DataTableRow key={id}>
                                    {/* <DataTableCell
                                        {...styles.orderWarehouseCells}
                                        data-test-selector="backOrderTable_tableCell_orderWarehouse"
                                    >
                                        {warehouse}
                                    </DataTableCell> */}
                                    <DataTableCell
                                        {...styles.orderCustomerIdCells}
                                        data-test-selector="backOrderTable_tableCell_orderCustomerId"
                                    >
                                        {customerNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderNumberCells}
                                        data-test-selector="backOrderTable_tableCell_orderNumber"
                                    >
                                        <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderLineNumberCells}
                                        data-test-selector="backOrderTable_tableCell_orderLineNumber"
                                    >
                                        {orderLineNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderProductErpCells}
                                        data-test-selector="backOrderTable_tableCell_orderProductErp"
                                    >
                                        {productErpNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderProductDescriptionCells}
                                        data-test-selector="backOrderTable_tableCell_orderProductDescription"
                                    >
                                        {productDescription}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderQuantityCells}
                                        data-test-selector="backOrderTable_tableCell_orderQuantity"
                                    >
                                        {backorderQuantity}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderLineStatusCells}
                                        data-test-selector="backOrderTable_tableCell_orderLineStatus"
                                    >
                                        {orderLineStatus}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.poNumberCells}
                                        data-test-selector="backOrderTable_tableCell_poNumber"
                                    >
                                        {poNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderActionsCells}
                                        data-test-selector="backOrderTable_tableCell_orderActions"
                                    >
                                        <StyledWrapper {...styles.actionsWrapper}>
                                            {cancelRequested ? (
                                                <Typography {...styles.cancelProductText}>
                                                    {translate("Cancel Requested")}
                                                </Typography>
                                            ) : (
                                                <Link
                                                    {...styles.cancelProductLink}
                                                    onClick={() => cancelProductModalHandler(backorder)}
                                                    aria-label="Cancel Products"
                                                >
                                                    {translate("Cancel Products")}
                                                </Link>
                                            )}
                                            {availableAltWarehouse && !isShippedFromAlternateDc && (
                                                <Link
                                                    {...styles.shipAltLink}
                                                    onClick={() =>
                                                        setShipFromAltDCModalIsOpen({ modalIsOpen: true, backorder })
                                                    }
                                                    aria-label="Ship From Alternate DC"
                                                >
                                                    {translate("Ship From Alternate DC")}
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            ) : (
                <StyledWrapper {...styles.noResultsContainer}>
                    {backOrders && (
                        <Notify {...styles.noResultsText} type="warning">
                            {translate("No orders found")}
                        </Notify>
                    )}
                </StyledWrapper>
            )}
            {!!pagination?.totalCount && (
                <Pagination
                    {...styles.pagination}
                    resultsCount={pagination?.totalCount || 0}
                    currentPage={pagination?.page || 1}
                    resultsPerPage={pagination?.pageSize || 12}
                    resultsPerPageOptions={[...Array(4).keys()].map(
                        (_, index) => pagination?.defaultPageSize * (index + 1),
                    )}
                    onChangePage={setCurrentPage}
                    onChangeResultsPerPage={setResultsPerPage}
                />
            )}
            <Modal
                {...styles.cancelProductModal}
                isOpen={!!cancelOrderModal}
                handleClose={() => setCancelOrderModal(false)}
            >
                <Typography {...styles.cancelProductModalTitle}>
                    {siteMessage("GUEST_BackOrder_Management_View")}
                </Typography>
                <Button
                    {...styles.cancelProductConfirmButton}
                    onClick={() => cancelProduct()}
                    aria-label="Yes, I want to cancel"
                >
                    {translate("Yes, I want to cancel")}
                </Button>
                <Button
                    {...styles.cancelProductCancelButton}
                    onClick={() => setCancelOrderModal(false)}
                    aria-label="No, I do not want to cancel"
                >
                    {translate("No, I do not want to cancel")}
                </Button>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BackorderManagement),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Backorder Management",
    },
};

export default widgetModule;
